import React from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { FeatureFlagUserSelectorProps } from '../../../types/features/featureFlags';
import { adminColors, adminTypography } from '../../../styles/adminDesignTokens';
import {
  FEATURE_FLAG_SELECTOR_LIST_SX,
  FEATURE_FLAG_SELECTOR_STATE_SX,
} from '../constants/featureFlagSelectorStyles';
import { useFeatureFlagUserStoreExpansion } from '../hooks/useFeatureFlagUserStoreExpansion';

const FeatureFlagUserSelector: React.FC<FeatureFlagUserSelectorProps> = ({
  hasNextPage,
  hasPreviousPage,
  onBulkUserAssignmentChange,
  onPageChange,
  onSearchChange,
  onUserAssignmentChange,
  page,
  remainingUsers,
  search,
  selectedUserIds,
  totalPages,
  totalUsers,
  userIdsError,
  users,
  usersError,
  usersLoading,
}) => {
  const { expandedUserIds, toggleUserStores } = useFeatureFlagUserStoreExpansion();
  const visibleUserIds = users.map((user) => user.id);
  const selectedUserIdSet = new Set(selectedUserIds);
  const selectedVisibleUserCount = visibleUserIds.filter((userId) => selectedUserIdSet.has(userId)).length;
  const hasVisibleUsers = visibleUserIds.length > 0;
  const bulkActionsDisabled = usersLoading || Boolean(usersError) || !hasVisibleUsers;

  return (
    <Stack spacing={1.25}>
      <TextField
        fullWidth
        label="Search Users"
        size="small"
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder="Search by user or store name..."
        sx={{
          '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff', borderRadius: '8px' },
        }}
      />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between' }}
      >
        <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText }}>
          {selectedUserIds.length} selected - {users.length} shown{totalUsers ? ` of ${totalUsers}` : ''}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
          <Button
            disabled={bulkActionsDisabled || selectedVisibleUserCount === visibleUserIds.length}
            onClick={() => onBulkUserAssignmentChange(visibleUserIds, true)}
            size="small"
            variant="text"
            sx={{
              color: adminColors.primary,
              fontSize: adminTypography.helper.fontSize,
              fontWeight: 600,
              minWidth: 0,
              p: 0,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            Select shown
          </Button>
          <Button
            disabled={bulkActionsDisabled || selectedVisibleUserCount === 0}
            onClick={() => onBulkUserAssignmentChange(visibleUserIds, false)}
            size="small"
            variant="text"
            sx={{
              color: adminColors.primary,
              fontSize: adminTypography.helper.fontSize,
              fontWeight: 600,
              minWidth: 0,
              p: 0,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            Clear shown
          </Button>
        </Stack>
      </Stack>

      {userIdsError ? (
        <Typography sx={{ ...adminTypography.helper, color: adminColors.danger }}>
          {userIdsError}
        </Typography>
      ) : null}

      <Box
        sx={FEATURE_FLAG_SELECTOR_LIST_SX}
      >
        {usersLoading ? (
          <Box sx={FEATURE_FLAG_SELECTOR_STATE_SX}>
            <CircularProgress size={24} />
          </Box>
        ) : usersError ? (
          <Box sx={FEATURE_FLAG_SELECTOR_STATE_SX}>
            <Typography sx={{ ...adminTypography.helper, color: adminColors.danger }}>
              {usersError}
            </Typography>
          </Box>
        ) : users.length === 0 ? (
          <Box sx={FEATURE_FLAG_SELECTOR_STATE_SX}>
            <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText }}>
              No users found.
            </Typography>
          </Box>
        ) : users.map((user) => (
          <Box
            key={user.id}
            sx={{
              borderBottom: `1px solid ${adminColors.divider}`,
              '&:last-of-type': { borderBottom: 0 },
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ px: 1, py: 0.75 }}
            >
              <Checkbox
                checked={selectedUserIdSet.has(user.id)}
                onChange={(event) => onUserAssignmentChange(user.id, event.target.checked)}
                size="small"
                sx={{ color: adminColors.primary, p: 0.25 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>
                  {user.name}
                </Typography>
                <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText }}>
                  {user.roleSummary}
                </Typography>
              </Box>
              <Chip
                label={`${user.ownedStoreCount} ${user.ownedStoreCount === 1 ? 'store' : 'stores'}`}
                size="small"
                sx={{
                  backgroundColor: adminColors.surfaceSubtle,
                  border: `1px solid ${adminColors.divider}`,
                  color: adminColors.text,
                  flexShrink: 0,
                  fontSize: adminTypography.helper.fontSize,
                  fontWeight: 600,
                  height: 24,
                }}
              />
              <IconButton
                aria-controls={`feature-flag-user-stores-${user.id}`}
                aria-expanded={expandedUserIds.has(user.id)}
                aria-label={`${expandedUserIds.has(user.id) ? 'Hide' : 'View'} stores for ${user.name}`}
                disabled={user.storeNames.length === 0}
                onClick={() => toggleUserStores(user.id)}
                size="small"
                sx={{
                  color: adminColors.primary,
                  flexShrink: 0,
                  p: 0.25,
                }}
              >
                {expandedUserIds.has(user.id)
                  ? <KeyboardArrowUpIcon fontSize="small" />
                  : <KeyboardArrowDownIcon fontSize="small" />}
              </IconButton>
            </Stack>
            {expandedUserIds.has(user.id) ? (
              <Box id={`feature-flag-user-stores-${user.id}`} sx={{ pb: 1, pl: 5.75, pr: 1 }}>
                <Stack
                  sx={{
                    backgroundColor: adminColors.surface,
                    border: `1px solid ${adminColors.divider}`,
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  {user.storeNames.map((storeName, storeIndex) => (
                    <Typography
                      key={`${user.id}-${storeName}-${storeIndex}`}
                      sx={{
                        ...adminTypography.helper,
                        backgroundColor: storeIndex % 2 === 0 ? adminColors.surfaceSubtle : adminColors.surface,
                        borderBottom: storeIndex === user.storeNames.length - 1
                          ? 0
                          : `1px solid ${adminColors.divider}`,
                        color: adminColors.text,
                        px: 1,
                        py: 0.65,
                      }}
                    >
                      {storeName}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            ) : null}
          </Box>
        ))}
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between' }}
      >
        <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText }}>
          Page {page} of {totalPages} - {remainingUsers} remaining
        </Typography>
        <Stack direction="row" spacing={1} sx={{ justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
          <Button
            disabled={usersLoading || Boolean(usersError) || !hasPreviousPage}
            onClick={() => onPageChange(page - 1)}
            size="small"
            variant="outlined"
            sx={{ minWidth: 72, textTransform: 'none' }}
          >
            Previous
          </Button>
          <Button
            disabled={usersLoading || Boolean(usersError) || !hasNextPage}
            onClick={() => onPageChange(page + 1)}
            size="small"
            variant="outlined"
            sx={{ minWidth: 72, textTransform: 'none' }}
          >
            Next
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default React.memo(FeatureFlagUserSelector);
